import { GetStaticPropsResult, Redirect } from 'next';
import { ParsedUrlQuery, encode } from 'querystring';

import {
  CommentUser,
  DISCUSSION_STATUS_UNMODERATED,
  DiscussionComment,
  DiscussionQuestion,
  DiscussionReview,
  DiscussionStatus,
  DiscussionType,
  IDiscussionUserInfoProps,
  IQuestionCommentProps,
  QuestionV2,
} from '~/lib/discussions/types';
import { fetchJson } from '~/lib/fetch';
import { CommentType, CommentV2Type } from '~/static/discussions/enums';
import { UserObjectProps } from '~/types/user';

interface Headers {
  [key: string]: string;
}

export function getCSRF(): string | null {
  const csrfPrefix = document.cookie?.split('csrftoken=');

  if (csrfPrefix?.length !== 2) {
    return null;
  }

  return csrfPrefix[1].split(';')[0];
}

export function getCommentV2(
  dealId: number,
  type: CommentV2Type,
  discussionId: number,
  urlPathSuffix: string = '',
  headers: Headers = {},
): Promise<DiscussionComment> {
  return fetchJson(
    `/api/v2/deals/${dealId}/${type}s/${discussionId}/?url_path_suffix=${urlPathSuffix}`,
    {
      headers,
    },
  );
}

export function getQuestionV2(
  dealId: number,
  discussionId: number,
  urlPathSuffix: string = '',
  headers: Headers = {},
): Promise<DiscussionQuestion> {
  return getCommentV2(
    dealId,
    CommentV2Type.Question,
    discussionId,
    urlPathSuffix,
    headers,
  ) as Promise<DiscussionQuestion>;
}

export function getReviewV2(
  dealId: number,
  discussionId: number,
  urlPathSuffix: string = '',
  headers: Headers = {},
): Promise<DiscussionReview> {
  return getCommentV2(
    dealId,
    CommentV2Type.Review,
    discussionId,
    urlPathSuffix,
    headers,
  ) as Promise<DiscussionReview>;
}

export function getComment(
  dealId: number,
  group: CommentType,
  discussionId: number,
  headers: Headers = {},
): Promise<DiscussionComment> {
  return fetchJson(
    `/discussions/api/listcomments/${dealId}/?group=${group}&thread_id=${discussionId}&for_discussion_page=True`,
    {
      headers,
    },
  );
}

export function redirectToHome(): GetStaticPropsResult<Redirect> {
  return {
    redirect: {
      statusCode: 302,
      destination: '/',
    },
  };
}

export function redirectToPdp(
  slug: string,
  hash?: string,
  query?: ParsedUrlQuery,
): GetStaticPropsResult<Redirect> {
  let queryString = '';

  if (query) {
    const queryParams = new URLSearchParams(encode(query));
    queryParams.delete('slug'); // query take url slug as query param

    queryString = `?${queryParams.toString()}`;
  }

  return {
    redirect: {
      statusCode: 302,
      destination:
        `/products/${slug}/` + (hash ? `#${hash}` : '') + (queryString || ''),
    },
  };
}

export const mapToQuestionComment = (
  question: QuestionV2,
  mainQuestionId: number,
  user: UserObjectProps,
  partnersIds: number[],
  isNew: boolean = false,
): IQuestionCommentProps => {
  const item = {
    ...question,
    status: question.status ?? DISCUSSION_STATUS_UNMODERATED,
  } as QuestionV2;

  if (!item.user) {
    item.user = { ...user, comment_whitelist: false } as CommentUser;
  }

  const userInfo: IDiscussionUserInfoProps = {
    id: item.user.id,
    username: item.user.username,
    avatar: item.user.avatar,
    isVerified: item.purchased,
    isPlus: item.user.has_plus ?? false,
    commentDate: new Date(item.created),
    isFounder: partnersIds.includes(item.user.id),
  };

  return {
    id: item.id,
    questionId: mainQuestionId,
    dealId: item.deal_id,
    text: item.comment,
    level: item.level,
    userInfo,
    status: item.status?.toLowerCase() as DiscussionStatus,
    comments: item.children?.map((child) =>
      mapToQuestionComment(child, mainQuestionId, user, partnersIds),
    ),
    isNew,
  } as IQuestionCommentProps;
};

export const getQAndRPageCanonicalUrl = (slug: string, type: DiscussionType) =>
  `/products/${slug}/${type}/`;
